import { cva } from "class-variance-authority";
import { AnimatePresence, motion } from "motion/react";
import { useId } from "react";
import { cn } from "../utils";
import type { ProcessButtonController } from "./use-process-button";

/** Props Interface */
interface ProcessButtonProps {
	controller: ProcessButtonController;
}

export function ProcessButton({ controller }: ProcessButtonProps) {
	/** Classes */
	const classes = processButton({ state: controller.state });
	const Icon = controller.getIcon(controller.state);

	/** Id */
	const id = useId();

	/** Render */
	return (
		<motion.button
			type="button"
			className={cn("process-button", classes)}
			onClick={controller.handleOnClick}
			disabled={controller.getIsDisabled()}
			animate={controller.state}
			variants={{
				processing: {
					width: "auto",
				},
				completed: {
					width: "auto",
				},
				pending: {
					width: "auto",
				},
				failed: {
					width: "auto",
					translateX: [0, -12, 12, -10, 10, -6, 6, -2, 0],
					transition: {
						duration: 0.7,
						ease: "easeInOut",
					},
				},
			}}
			transition={{ duration: 0.85, ease: "circInOut" }}
		>
			{controller.state === "processing" && <div className="absolute inset-0 shimmer" />}
			<motion.div
				layoutId={`process-button-icon-${id}`}
				className={cn(
					"process-button-icon",
					"pointer-events-none",
					"size-8 flex flex-center bg-white dark:bg-primary-50 relative z-1",
					"shadow-sm",
				)}
				transition={{ duration: 0.15, ease: "circInOut" }}
			>
				<AnimatePresence mode="wait">
					<motion.div
						className="translate-x-[-0.5px] translate-y-[0.5px]"
						key={controller.state}
						initial={{ scale: 0, rotate: 45 }}
						animate={{ scale: 1, rotate: 0 }}
						exit={{ scale: 0, rotate: 45 }}
						transition={{ duration: 0.25, ease: "circInOut" }}
					>
						<Icon />
					</motion.div>
				</AnimatePresence>
			</motion.div>
			<motion.span
				layoutId={`process-button-label-${id}`}
				layout="position"
				className="mx-auto mr-4 text-sm"
				transition={{ duration: 0.15, ease: "circInOut" }}
			>
				{controller.getLabel()}
			</motion.span>
		</motion.button>
	);
}

const processButton = cva(
	[
		"text-white",
		"h-stack gap-3 pr-7",
		"cursor-pointer",
		"border-[0.5px]",
		"transition-colors",
		"relative",
		"overflow-hidden",
		"disabled:cursor-not-allowed",
	],
	{
		variants: {
			state: {
				pending: [
					"border-color",
					"bg-radial-[at_0%_0%] from-primary-700 to-primary-800",
					"ring-primary-500/10",
				],
				processing: [
					"border-color",
					"bg-radial-[at_0%_0%] from-primary-700 to-primary-800",
					"ring-primary-500/10",
				],
				completed: [
					"border-color",
					"bg-linear-65 from-emerald-500 to-emerald-600",
					"ring-emerald-500/10 dark:ring-emerald-500/20",
				],
				failed: [
					"border-color",
					"bg-radial-[at_0%_0%] from-rose-500 to-red-600",
					"ring-red-500/10",
				],
			},
		},
	},
);
